import * as React from 'react';
import { cn } from '@/lib/utils';

const StyledTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn(
        'w-full caption-bottom text-sm',

        'relative',
        // Pseudo-element for rounded border
        "before:content-[''] before:absolute before:inset-0 before:rounded-xl before:border before:border-border before:pointer-events-none before:z-1 before:bg-zinc-100",

        className
      )}
      {...props}
    />
  </div>
));
StyledTable.displayName = 'StyledTable';

const StyledTableWrapper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('relative', className)} {...props}>
    {children}
  </div>
));
StyledTableWrapper.displayName = 'StyledTableWrapper';

const StyledTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('', className)} {...props} />
));
StyledTableHeader.displayName = 'StyledTableHeader';

const StyledTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      'relative',
      // Pseudo-element for rounded border
      "before:content-[''] before:absolute before:inset-0 before:rounded-xl before:border before:border-border before:pointer-events-none before:z-10",
      className
    )}
    {...props}
  />
));
StyledTableBody.displayName = 'StyledTableBody';

const StyledTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'transition-colors group',
      '[&>td]:border-0', // Remove individual cell borders since we're using tbody border
      className
    )}
    {...props}
  />
));
StyledTableRow.displayName = 'StyledTableRow';

const StyledTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'relative z-0 first:pl-6 pl-4 last:pr-6 pr-4 py-3 text-left text-xs font-medium text-muted-foreground ',
      '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
));
StyledTableHead.displayName = 'StyledTableHead';

const StyledTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'relative z-0 first:pl-6 pl-4 last:pr-6 pr-4 py-4 text-sm font-medium align-middle',
      '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      // Pseudo-element background for all states
      "before:content-[''] before:absolute before:inset-0 before:bg-background before:pointer-events-none before:-z-10 ",
      // Hover background color change
      'group-hover:before:bg-slate-50',
      // Selected background color change
      'group-[.selected]:before:bg-blue-50 dark:group-[.selected]:before:bg-blue-950/20',
      // "group-hover:before:border-primary",
      // "group-hover:first:before:rounded-l-md",
      // "group-hover:before:border",
      // Rounded corners for corner cells
      '[tr:not(:last-child)>&]:before:border-b',
      '[tr:not(:last-child)>&]:before:border-zinc-100',
      '[tr:first-child>&]:first:before:rounded-tl-lg',
      '[tr:first-child>&]:last:before:rounded-tr-lg',
      '[tr:last-child>&]:first:before:rounded-bl-lg',
      '[tr:last-child>&]:last:before:rounded-br-lg',
      className
    )}
    {...props}
  />
));
StyledTableCell.displayName = 'StyledTableCell';

export {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
};
